import {
  collection, onSnapshot, query, where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/config/firebase';
import { isRentalAwaitingConfirmation } from '@/utils/rentalAwaitingConfirmation';

/** „YYYY-MM-DD” (UTC), tak jak klucze dni w kolekcjach śniadań i spa. */
const dayKey = (offsetDays = 0): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

/**
 * Liczby NOWYCH zamówień, rezerwacji i zgłoszeń do sidebara, na żywo (ścieżka menu → liczba).
 *
 * Firestore nie ma agregacji na żywo, więc słuchamy zapytań zawężonych do samych „nowych”
 * (`where status == …`) i bierzemy `snapshot.size`. Odczyty: przy starcie tyle, ile jest
 * nowych dokumentów (zwykle kilka), potem tylko dokumenty, które wchodzą do zbioru albo
 * z niego wychodzą. Nie czytamy pozostałych zamówień. Śniadania i spa są podzielone
 * na dni: śniadania liczymy na jutro (domyślny dzień listy), spa na dziś.
 */
export const useNewOrdersCounts = (organizationId: string | null | undefined, enabled = true) => {
  const [data, setData] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!organizationId || !enabled) return undefined;
    const base = `hotels/${organizationId}`;
    const sources: Array<[string, string, string]> = [
      ['/orders', `${base}/orders`, 'new'],
      ['/order-products', `${base}/product-orders`, 'new'],
      ['/breakfast-orders', `${base}/breakfastOrders/${dayKey(1)}/orders`, 'new'],
      ['/reservations/wellness', `${base}/spaReservations/${dayKey(0)}/items`, 'pending'],
      ['/product-rental-reservations', `${base}/productRentalsReservations`, 'new'],
      ['/reports', `${base}/tasks`, 'new'],
    ];
    const unsubs = sources.map(([menuPath, path, status]) => onSnapshot(
      query(collection(db, path), where('status', '==', status)),
      (snap) => {
        // Wypożyczalnia: bez online nieopłaconych (lista pokazuje je jako „Nieopłacona”).
        const size = path.endsWith('/productRentalsReservations')
          ? snap.docs.filter((d) => isRentalAwaitingConfirmation(d.data())).length
          : snap.size;
        setData((prev) => (prev[menuPath] === size ? prev : { ...prev, [menuPath]: size }));
      },
      // Brak uprawnień albo kolekcji: po prostu bez liczby przy tej pozycji.
      () => setData((prev) => ({ ...prev, [menuPath]: 0 })),
    ));
    return () => unsubs.forEach((u) => u());
  }, [organizationId, enabled]);

  return { data };
};
